import DeleteIcon from "@mui/icons-material/Delete";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { Box, Button, Typography } from "@mui/material";

export interface SelectionToolbarProps {
  /** Number of documents currently selected */
  selectedCount: number;
  /** Total number of documents matching current filters */
  totalFilteredCount: number;
  /** Callback when delete button is clicked */
  onDeleteClick: () => void;
  /** Callback when reset all button is clicked */
  onResetAllClick: () => void;
  /** Whether delete operation is in progress */
  isDeleting?: boolean;
}

/**
 * Toolbar displayed when documents are selected,
 * showing selection count and delete button
 */
export function SelectionToolbar({
  selectedCount,
  totalFilteredCount,
  onDeleteClick,
  onResetAllClick,
  isDeleting = false,
}: SelectionToolbarProps) {
  const isDeleteDisabled = selectedCount === 0 || isDeleting;
  const isResetDisabled = totalFilteredCount === 0 || isDeleting;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 1,
        px: 2,
        backgroundColor: selectedCount > 0 ? "action.selected" : "background.paper",
        borderBottom: 1,
        borderColor: "divider",
        minHeight: 48,
      }}
    >
      <Typography
        variant="body2"
        color={selectedCount > 0 ? "primary" : "text.secondary"}
        fontWeight={selectedCount > 0 ? 500 : 400}
      >
        {selectedCount === 0
          ? "No documents selected"
          : `${selectedCount.toLocaleString()} document${selectedCount !== 1 ? "s" : ""} selected`}
      </Typography>

      <Box sx={{ display: "flex", gap: 1 }}>
        <Button
          variant="outlined"
          color="error"
          size="small"
          startIcon={<RestartAltIcon />}
          onClick={onResetAllClick}
          disabled={isResetDisabled}
          sx={{
            textTransform: "none",
          }}
        >
          {isDeleting ? "Deleting..." : `Delete All (${totalFilteredCount.toLocaleString()})`}
        </Button>

        <Button
          variant="contained"
          color="error"
          size="small"
          startIcon={<DeleteIcon />}
          onClick={onDeleteClick}
          disabled={isDeleteDisabled}
          sx={{
            textTransform: "none",
          }}
        >
          {isDeleting ? "Deleting..." : `Delete Selected (${selectedCount})`}
        </Button>
      </Box>
    </Box>
  );
}
